import ItemFactory from "../components/Map/itemFactory"
import RadioCacheHelper from "./radioCacheHelper";
import RadioCacheWorker from "worker-loader!./radioCacheWorker";

const DEXIE_NOT_AVAILABLE = "Dexie not available, cannot read or write to storage, perhaps IndexedDB is not available?"
const TWELVE_HOURS_IN_MS = 12 * 3600 * 1000

export default {
    databaseName: "dexie-netmet-radios",
    userIdCustomerKey: "userIdCustomerKey",
    batchFetchSize: 100000,
    clearOutStorageOnLogin: true,
    lastRadioWriteTimestampKey: "lastRadioWriteTimestamp",

    /**
     * This function will attempt to initialize Dexie, open a connection and if the userId/customer
     * pair passed in do not match what is in the users table, then clear out the radios table and
     * write the new userId/customer to the users table.
     * @param userId
     * @param customer
     * @param db
     * @returns {Promise<unknown>}
     */
    init(userId, customer, db) {
        let RadioCacheService = this
        RadioCacheService.isInitialized = false

        return new Promise((resolve, reject) => {
            // check and see if indexedDB is supported OR if a db was passed into this function (for testing)
            if (window.indexedDB || db) {
                // if someone passes us a db then use that one to init the DB.  This is done for testing as Dexie
                // needs to use a mocked up IndexedDB.  When init() is called from index.js, no db parameter is passed in.
                RadioCacheHelper.initDB(db).then((_db) => {
                    RadioCacheService.db = _db;
                    // if we have a userId and customer, check for that user in the db
                    if (userId && customer) {
                        // create the radio cache worker, used to write radios in it's own thread
                        RadioCacheService.radioCacheWorker = new RadioCacheWorker();
                        RadioCacheService.radioCacheWorker.onmessage = function (e) {
                            // console.debug('[RadioCacheService] Message received from worker - ' + e.data);
                        }
                        // see if there is a user in the db, and also get the radio write timestamp
                        let promises = []
                        promises.push(RadioCacheService.readKeyValueFromStorage(RadioCacheService.userIdCustomerKey))
                        promises.push(RadioCacheService.readRadioTimestampFromStorage())

                        Promise.all(promises).then((results) => {
                            if (RadioCacheService.shouldClearOutRadioCache(results[0], userId, customer, results[1])) {
                                // clear out the key_value table, write the new user, then clear out the radios
                                RadioCacheService.clearKeyValueDataFromStorage().then(function (result) {
                                    return RadioCacheService.writeKeyValueToStorage(RadioCacheService.userIdCustomerKey, {
                                        'userId': userId,
                                        'customer': customer
                                    })
                                }).then(function (result) {
                                    return RadioCacheService.clearRadioDataFromStorage()
                                }).then(function (result) {
                                    RadioCacheService.isInitialized = true
                                    resolve("success")
                                }).catch(function (err) {
                                    console.error('RadioCacheService failed to clear radios, user tables and write new user: ' + (err.stack || err))
                                    reject(err)
                                })
                            } else {
                                RadioCacheService.isInitialized = true
                                resolve("success")
                            }
                        }).catch(function (err) {
                            console.error('RadioCacheService failed to read key value pairs for user and radio timestamp: ' + (err.stack || err))
                            reject(err)
                        })
                    } else {
                        // just init the DB and resolve
                        RadioCacheService.isInitialized = true
                        resolve("success")
                    }
                }).catch(function (err) {
                    RadioCacheService.isInitialized = true
                    console.error('RadioCacheService failed to open db: ' + (err.stack || err))
                    reject(err)
                })
            } else {
                // even thou indexedDB is not supported, just init the DB and resolve
                RadioCacheService.isInitialized = true
                resolve("success")
            }
        })
    },

    /**
     * This function will attempt to lookup the record from the key_value table using the key passed,
     * and return it in a Promise.
     * @returns {Promise<user object>}
     */
    readKeyValueFromStorage(key) {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {
                RadioCacheService.db.key_value.where("id").equals(key).toArray(function (values) {
                    // there should be at most 1 row for this key in this table, so return the 1st one
                    if (values && values[0]) {
                        if(typeof values[0].value === 'string') {
                            //being very defensive in case we stored a string that is not valid JSON
                            //even if all our current use cases are JSON-safe 
                            //(user obj = plain object of strings or numbers, timestamp = number, viewing rectangle = plan object of numbers)
                            try {
                                resolve(JSON.parse(values[0].value))
                            } catch (e) {
                                resolve(values[0].value)
                            }
                        } else {
                            resolve(values[0].value)
                        }
                    } else {
                        resolve(null)
                    }
                }).catch(function (err) {
                    reject(err)
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function will attempt to write the given key value pair to the key_value table in IndexedDB.
     * @param key
     * @param value
     * @returns {Promise<unknown>}
     */
    writeKeyValueToStorage(key, value) {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {

                RadioCacheService.db.key_value.put({'id': key, 'value': JSON.stringify(value)}).then(function (lastKey) {
                    resolve("success")
                }).catch(function (err) {
                    reject(err)
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function will attempt to clear out all data in the key_value table in IndexedDB.
     * @returns {Promise<unknown>}
     */
    clearKeyValueDataFromStorage() {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {
                RadioCacheService.db.key_value.clear().then(function () {
                    resolve("success")
                }).catch(function (err) {
                    reject(err)
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function will attempt to fetch radios from IndexedDB and return them in a Promise
     * @returns {Promise<radios Array>}
     */
    readRadiosFromStorage() {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {

                RadioCacheService.db.radios.count().then(function (count) {
                    if (count > 0 && count > RadioCacheService.batchFetchSize) {
                        // fetch radios by batch size
                        let promises = []
                        let radios = []
                        let x = Math.ceil(count / RadioCacheService.batchFetchSize)
                        for (let i = 0; i < x; i++) {
                            promises.push(RadioCacheService.db.radios.offset(i * RadioCacheService.batchFetchSize).limit(RadioCacheService.batchFetchSize).toArray())
                        }
                        Promise.all(promises).then(items => {
                            items.forEach(item => {
                                radios = radios.concat(item)
                            })
                            resolve(radios)
                        }).catch(function (err) {
                            reject(err)
                        })
                    } else {
                        // fetch all of the radios
                        RadioCacheService.db.radios.toArray().then(function (values) {
                            resolve(values)
                        }).catch(function (err) {
                            reject(err)
                        })
                    }
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function is to write the current timestamp to storage, which should be called each
     * time a radio is written to storage.
     * @returns {*|Promise<unknown>}
     */
    writeRadioTimestampToStorage() {
        let RadioCacheService = this
        return RadioCacheService.writeKeyValueToStorage(RadioCacheService.lastRadioWriteTimestampKey, Date.now())
    },

    /**
     * This function is to read the timestamp for the last radio write to storage.
     * @returns {*|Promise<user, Object>}
     */
    readRadioTimestampFromStorage() {
        let RadioCacheService = this
        return RadioCacheService.readKeyValueFromStorage(RadioCacheService.lastRadioWriteTimestampKey)
    },

    /**
     * This function will take radios in a geoJson format and write them to the radios table in IndexedDB
     * and write a timestamp for the radio write to storage.
     * @param radios
     * @returns {Promise<unknown>}
     */
    writeRadiosToStorage(radios) {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {
                RadioCacheService.db.radios.bulkPut(radios).then(function (lastKey) {
                    return RadioCacheService.writeRadioTimestampToStorage()
                }).then(function (result) {
                    resolve("success")
                }).catch(function (err) {
                    reject(err)
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function will write a timestamp for the radio write to storage and then attempt to take the
     * radios in a geoJson format and write them to the radios table in IndexedDB using a web worker to
     * perform the write in a separate thread as to not block the UI thread.
     * @param radios
     * @returns {Promise<unknown>}
     */
    writeRadiosToStorageAsync(radios) {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {
                RadioCacheService.writeRadioTimestampToStorage().then(function (lastKey) {
                    if (RadioCacheService.radioCacheWorker && radios) {
                        // Attempt to send a plain, serializable copy to the worker. Vue reactive
                        // objects and other complex references can cause DataCloneError when
                        // using postMessage; JSON-serialize as a safe fallback.
                        const safeCopy = JSON.parse(JSON.stringify(radios));
                        RadioCacheService.radioCacheWorker.postMessage([safeCopy]);
                    }
                    resolve("success")
                }).catch(function (err) {
                    reject(err)
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    radioHasValidGPSCoords(radio) {
        if (radio && radio.geometry && radio.geometry.coordinates) {
            if (radio.geometry.coordinates[0] !== null && radio.geometry.coordinates[1] !== null && radio.geometry.estimatedLatLong !== undefined) {
                return true;
            }
            else {
                return false;
            }
        }
    },

    /**
     * This function will take the raw radio data (with valid GPS Coords) returned from the radio service API,
     * call the itemFactory to turn the radios into geoJson objects, write the radios to IndexedDB, and write
     * a timestamp for the radio write to storage.  Radios without valid GPS Coords will NOT be written to IndexedDB.
     * @param rawRadios
     */
    writeRawRadiosToStorage(rawRadios) {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {
                ItemFactory(rawRadios, false, "radio").then(values => {
                    let valuesToWrite = []
                    values.forEach(value => {
                        if (RadioCacheService.radioHasValidGPSCoords(value)) {
                            valuesToWrite.push(value)
                        }
                    })
                    RadioCacheService.writeRadiosToStorage(valuesToWrite).then((result) => {
                        return RadioCacheService.writeRadioTimestampToStorage()
                    }).then(function (result) {
                        resolve(result)
                    }).catch(function (err) {
                        reject(err)
                    })
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function will attempt to clear all data from the radios table in IndexedDB
     * @returns {Promise<unknown>}
     */
    clearRadioDataFromStorage() {
        let RadioCacheService = this
        return new Promise((resolve, reject) => {
            if (RadioCacheService.isAvailable()) {
                RadioCacheService.db.radios.clear().then(function () {
                    resolve("success")
                }).catch(function (err) {
                    reject(err)
                })
            } else {
                // reject if dexie is not available
                reject(DEXIE_NOT_AVAILABLE)
            }
        })
    },

    /**
     * This function will check and see if Dexie has been initialized and the is not in a Failed state.
     * When the browser does not support IndexedDB this function will always return false.
     * @returns {boolean}
     */
    isAvailable() {
        let RadioCacheService = this
        if (RadioCacheService.db) {
            return RadioCacheService.db && !RadioCacheService.db.hasFailed()
        } else {
            return false
        }
    },

    shouldClearOutRadioCache(userFromStorage, userId, customer, lastRadioWriteTimestamp) {
        if (!userFromStorage || (userFromStorage.userId !== userId || userFromStorage.customer !== customer)) {
            // if we don't have a user OR if the userId or customer changed then we need to clear out radio storage
            return true
        }
        return lastRadioWriteTimestamp && lastRadioWriteTimestamp < (Date.now() - TWELVE_HOURS_IN_MS);

    }

}
